"use client";

import { useEffect, useMemo, useState } from "react";
import { useComponentTranslations, useI18n, type BookingFormTranslationKey } from "@/app/components/LanguageProvider";
import { useSession } from "next-auth/react";
import { getCookie } from "@/lib/cookie-utils";
import { getPathWithLocale } from "@/lib/i18n";
import Link from "next/link";

type BookingFormProps = {
  initialEmail?: string;
  onSuccessAction?: (email: string) => void;
  showSuccessLink?: boolean;
};

export default function BookingForm({
  initialEmail = "",
  onSuccessAction,
  showSuccessLink = true,
}: BookingFormProps) {
  const { t: bookingFormT, translationsLoaded } = useComponentTranslations("BookingForm");
  const { lang } = useI18n();
  const { data: session } = useSession();
  const user = session?.user;

  const bf = (key: BookingFormTranslationKey, fallback?: string) => {
    if (!translationsLoaded) return fallback ?? key;
    return bookingFormT(key, fallback) ?? fallback ?? key;
  };
  
  const userEmail = useMemo(() => {
    if (!user?.email) return "";
    return user.email.trim().toLowerCase();
  }, [user?.email]);
  const [status, setStatus] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: initialEmail,
    datetime: "",
    purpose: "",
  });

  useEffect(() => {
    if (userEmail) {
      setForm((prev) => ({ ...prev, email: userEmail }));
      return;
    }
    const nextEmail = initialEmail.trim().toLowerCase();
    setForm((prev) => ({ ...prev, email: nextEmail }));
  }, [initialEmail, userEmail]);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);
    setIsSubmitting(true);

    try {
      const emailToSubmit = (userEmail || form.email).trim().toLowerCase();
      const token = getCookie("token");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch("/api/book", {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ ...form, email: emailToSubmit }),
      });

      if (res.ok) {
        await res.json();
        setStatus(bf("bookingSuccess"));
        setForm({ name: "", email: userEmail || initialEmail, datetime: "", purpose: "" });
        onSuccessAction?.(emailToSubmit);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error("Booking failed:", errorData);
        setStatus(bf("bookingError"));
      }
    } catch {
      setStatus(bf("generalError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-xs text-white/70">{bf("name")}</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1 w-full rounded-lg bg-white/10 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/60 outline-none"
          placeholder={bf("yourName")}
        />
      </div>
      <div>
        <label className="block text-xs text-white/70">{bf("email")}</label>
        <input
          type="email"
          value={userEmail || form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          disabled={Boolean(userEmail)}
          className="mt-1 w-full rounded-lg bg-white/10 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/60 outline-none"
          placeholder="your@email.com"
        />
      </div>
      <div>
        <label className="block text-xs text-white/70">{bf("date")}</label>
        <input
          required
          type="datetime-local"
          value={form.datetime}
          onChange={(e) => setForm({ ...form, datetime: e.target.value })}
          className="mt-1 w-full rounded-lg bg-white/10 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/60 outline-none"
        />
      </div>
      <div>
        <label className="block text-xs text-white/70 mb-2">{bf("callPurpose")}</label>
        <p className="text-xs text-white/60 mb-2">{bf("callPurposePlaceholder")}</p>
        <textarea
          required
          rows={5}
          value={form.purpose}
          onChange={(e) => setForm({ ...form, purpose: e.target.value })}
          className="mt-1 w-full rounded-lg bg-white/10 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/60 outline-none resize-none"
          placeholder={bf("callPurposeExample")}
        />
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition-colors"
      >
        {isSubmitting ? bf("submitting") : bf("submit")}
      </button>
      {status && (
        <div
          className={`text-xs p-3 rounded-lg ${
            status.includes("✅") || status.includes("success") || status.includes("успешно")
              ? "bg-green-500/20 border border-green-500/30 text-green-300"
              : "bg-red-500/20 border border-red-500/30 text-red-300"
          }`}
        >
          {status}
          {showSuccessLink && (status.includes("✅") || status.includes("success") || status.includes("успешно")) && (
              <p className="text-xs mt-2 text-white/70">
                {bf("viewInDashboard")}{" "}
                <Link href={getPathWithLocale("/dashboard/my-bookings", lang)} className="underline hover:text-white">
                  {bf("myBookings")}
                </Link>
              </p>
          )}
        </div>
      )}
    </form>
  );
}
